import React, { useCallback, useContext, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { Role, hasMfaExcludedRole } from "src/network/User";
import { MfaBulkResult } from "src/network/AuthClient";
import styles from "src/screens/customerusers/mfaBulk/CustomerUsersMfaBulk.module.css";

export interface MfaBulkRoleOption {
    /** The portal role name the backend filters by, e.g. "coowner". */
    key: string;
    roleid: number;
    label: string;
}

/** The part of a customer user the MFA switch state is derived from. */
export interface MfaBulkUser {
    registered: boolean;
    mfaenabled: boolean;
    userroles: { role: number }[];
    /** MFA.HASPERMISSION rows; the value "false" means MFA is mandatory for the user. */
    mfaPermission: { value: string | null }[];
}

interface CustomerUsersMfaBulkProps {
    roleOptions: MfaBulkRoleOption[];
    users: MfaBulkUser[];
    /** Called after an action ran, so the user list can be reloaded. */
    onChanged?: () => void;
    /** Called with the portal role key picked in the dropdown ("" = all users), so the user list can follow it. */
    onRoleChange?: (roleKey: string) => void;
}

enum BulkAction {
    ENABLE = "enable",
    DISABLE = "disable",
    MANDATORY_ON = "mandatory_on",
    MANDATORY_OFF = "mandatory_off"
}

const ALL_USERS = "";

/** One line of the outcome of an enable/disable run, shown in the card instead of as a toast. */
interface MfaBulkResultLine {
    text: string;
    type: MessageType;
}

const RESULT_CLASS: Partial<Record<MessageType, string>> = {
    [MessageType.SUCCESS]: "text-success",
    [MessageType.ERROR]: "text-danger",
    [MessageType.INFO]: "text-muted"
};

/**
 * Administrator only, behind FEATUREFLAG.MFA: switches MFA, or the "MFA is mandatory" setting, for every user of the customer or
 * for the holders of one portal role (#23478). The single-user case lives on the user details screen.
 */
const CustomerUsersMfaBulkBase = (props: CustomerUsersMfaBulkProps) => {
    const { t } = useTranslation();
    const { authStore, uiStore, navStore } = useContext(RootStoreContext);
    const [selectedRole, setSelectedRole] = useState<string>(ALL_USERS);
    const [pendingAction, setPendingAction] = useState<BulkAction | undefined>(undefined);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [resultLines, setResultLines] = useState<MfaBulkResultLine[]>([]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggleExpanded = useCallback(() => setIsExpanded((expanded) => !expanded), []);

    const isAdministrator = authStore.user?.availableRoles?.includes(Role.ADMINISTRATOR) ?? false;

    const roleItems = useMemo(
        () => [
            { label: t("screens.customer_users.mfa_bulk_role_all").toString(), value: ALL_USERS },
            ...props.roleOptions.map((role) => ({ label: role.label, value: role.key }))
        ],
        [props.roleOptions, t]
    );

    const selectedRoleLabel = roleItems.find((item) => item.value === selectedRole)?.label ?? "";

    // MFA is "on" when every registered user of the selection has it; unregistered users are skipped by the backend.
    const registeredInScope = useMemo(() => {
        const roleId = props.roleOptions.find((role) => role.key === selectedRole)?.roleid;

        return props.users.filter(
            (user) =>
                user.registered &&
                !hasMfaExcludedRole(user.userroles) &&
                (roleId === undefined || user.userroles.some((userRole) => userRole.role === roleId))
        );
    }, [props.roleOptions, props.users, selectedRole]);

    // Inspection app users never count for the MFA switches; tell the administrator when the selection contains some.
    const hasExcludedInScope = useMemo(() => {
        const roleId = props.roleOptions.find((role) => role.key === selectedRole)?.roleid;

        return props.users.some(
            (user) =>
                hasMfaExcludedRole(user.userroles) &&
                (roleId === undefined || user.userroles.some((userRole) => userRole.role === roleId))
        );
    }, [props.roleOptions, props.users, selectedRole]);

    const mfaSwitch = registeredInScope.length > 0 && registeredInScope.every((user) => user.mfaenabled);

    // Same rule as the user details: MFA.HASPERMISSION = "false" means mandatory.
    const mandatorySwitch =
        registeredInScope.length > 0 &&
        registeredInScope.every((user) => user.mfaPermission.some((setting) => setting.value === "false"));

    const scope =
        selectedRole === ALL_USERS
            ? t("screens.customer_users.mfa_bulk_scope_all").toString()
            : t("screens.customer_users.mfa_bulk_scope_role", { role: selectedRoleLabel }).toString();

    const runAction = useCallback(
        async (action: BulkAction) => {
            const role = selectedRole === ALL_USERS ? undefined : selectedRole;
            const client = authStore.authClient;
            const fail = () =>
                uiStore.printStatusMessage(t("screens.customer_users.mfa_bulk_error"), MessageType.ERROR);

            const skippedAndFailedLines = (result: MfaBulkResult): MfaBulkResultLine[] => [
                ...(result.skipped.length > 0
                    ? [
                          {
                              text: t("screens.customer_users.mfa_bulk_skipped", { count: result.skipped.length }),
                              type: MessageType.INFO
                          }
                      ]
                    : []),
                ...(result.failed.length > 0
                    ? [
                          {
                              text: t("screens.customer_users.mfa_bulk_failed", { count: result.failed.length }),
                              type: MessageType.ERROR
                          }
                      ]
                    : [])
            ];

            if (action === BulkAction.ENABLE || action === BulkAction.DISABLE) {
                const result = await client.toggleMfaForCustomer(action === BulkAction.ENABLE, role);
                if (!result) {
                    fail();
                    return;
                }

                // Nothing changed (e.g. everyone is mandatory) is no success: shown as an error, in the card.
                setResultLines([
                    {
                        text: t(`screens.customer_users.mfa_bulk_${action}_success`, {
                            count: result.toggled,
                            total: result.total
                        }),
                        type: result.failed.length === 0 && result.toggled > 0 ? MessageType.SUCCESS : MessageType.ERROR
                    },
                    ...skippedAndFailedLines(result)
                ]);
                return;
            }

            // Like on the user details: making MFA mandatory also switches MFA on first.
            let enableResult: MfaBulkResult | undefined;
            if (action === BulkAction.MANDATORY_ON) {
                enableResult = await client.toggleMfaForCustomer(true, role);
                if (!enableResult) {
                    fail();
                    return;
                }
            }

            const updated = await client.setMfaMandatoryForCustomer(action === BulkAction.MANDATORY_ON, role);
            if (updated === undefined) {
                fail();
                return;
            }

            setResultLines([
                {
                    text: t(`screens.customer_users.mfa_bulk_${action}_success`, { count: updated }),
                    type: MessageType.SUCCESS
                },
                ...(enableResult ? skippedAndFailedLines(enableResult) : [])
            ]);
        },
        [authStore.authClient, selectedRole, t, uiStore]
    );

    const confirm = useCallback(async () => {
        if (!pendingAction) {
            return;
        }

        setIsRunning(true);
        setResultLines([]);
        try {
            await runAction(pendingAction);
        } finally {
            setIsRunning(false);
            setPendingAction(undefined);
            props.onChanged?.();
        }
    }, [pendingAction, runAction, props]);

    const closeModal = useCallback(() => {
        if (!isRunning) {
            setPendingAction(undefined);
        }
    }, [isRunning]);

    // Only customers with FEATUREFLAG.MFA see the MFA administration, and only their administrators.
    if (!isAdministrator || !navStore.hasFeatureFlagMFA) {
        return null;
    }

    const rowStyle: React.CSSProperties = {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.5rem 0"
    };

    // The label sits at a fixed width so the switches line up, right next to the text.
    const labelStyle: React.CSSProperties = { flex: "0 0 11rem", maxWidth: "100%" };

    return (
        <>
            {/* The whole card toggles; the opened content stops the click so its controls keep working. */}
            <div className={styles.Clickable} onClick={toggleExpanded}>
                <UI.Card
                    title={t("screens.customer_users.mfa_bulk_title").toString()}
                    className={`mb-3 ${styles.Card}`}
                    flex={true}
                >
                    <button type="button" className={styles.ToggleButton} aria-expanded={isExpanded}>
                        <UI.Icon icon={isExpanded ? UI.SVGIcon.ChevronUpThin : UI.SVGIcon.ChevronDownThin} size={42} />
                    </button>
                    <div style={{ opacity: 0.75, marginBottom: "0.5rem" }}>
                        {t("screens.customer_users.mfa_bulk_description")}
                    </div>
                    {isExpanded && (
                        <div id="mfa-bulk-body" className={styles.Body} onClick={(event) => event.stopPropagation()}>
                            <div style={rowStyle}>
                                <span style={labelStyle}>{t("screens.customer_users.mfa_bulk_scope_label")}</span>
                                <UI.Dropdown
                                    id="mfa-bulk-role"
                                    label={selectedRoleLabel}
                                    items={roleItems}
                                    onSelect={(role: string) => {
                                        setSelectedRole(role);
                                        setResultLines([]);
                                        props.onRoleChange?.(role);
                                    }}
                                />
                            </div>
                            <div style={rowStyle}>
                                <span style={labelStyle}>{t("screens.customer_users.mfa_activate")}</span>
                                <UI.Switch
                                    checked={mfaSwitch}
                                    onToggle={() =>
                                        setPendingAction(mfaSwitch ? BulkAction.DISABLE : BulkAction.ENABLE)
                                    }
                                    disabled={isRunning || registeredInScope.length === 0}
                                />
                            </div>
                            <div style={rowStyle}>
                                <UI.OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <UI.Tooltip id="mfa-bulk-mandatory-tooltip">
                                            {t("screens.customer_users.mfa_mandatory_tooltip")}
                                        </UI.Tooltip>
                                    }
                                >
                                    <span style={labelStyle}>{t("screens.customer_users.mfa_mandatory")}</span>
                                </UI.OverlayTrigger>
                                <UI.Switch
                                    checked={mandatorySwitch}
                                    onToggle={() =>
                                        setPendingAction(
                                            mandatorySwitch ? BulkAction.MANDATORY_OFF : BulkAction.MANDATORY_ON
                                        )
                                    }
                                    disabled={isRunning || registeredInScope.length === 0}
                                />
                            </div>

                            {hasExcludedInScope && (
                                <div className="text-muted" style={{ padding: "0.5rem 0" }} role="note">
                                    {t("screens.customer_users.mfa_inspection_user_warning")}
                                </div>
                            )}

                            {resultLines.length > 0 && (
                                <div style={{ padding: "0.5rem 0" }} role="status">
                                    {resultLines.map((line) => (
                                        <div key={line.text} className={RESULT_CLASS[line.type]}>
                                            {line.text}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </UI.Card>
            </div>

            <UI.Modal
                backdrop={"static"}
                title={t("screens.customer_users.mfa_bulk_confirm_title").toString()}
                show={pendingAction !== undefined}
                onClose={closeModal}
            >
                <div>
                    {pendingAction && <p>{t(`screens.customer_users.mfa_bulk_confirm_${pendingAction}`, { scope })}</p>}
                    {isRunning ? (
                        <UI.RotatingSpinner noLogo={true} size={35} className={"align-items-start"} />
                    ) : (
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <UI.Button label={t("labels.yes")} variant="primary" onClick={confirm} />
                            <UI.Button label={t("labels.cancel")} variant="secondary" onClick={closeModal} />
                        </div>
                    )}
                </div>
            </UI.Modal>
        </>
    );
};

export const CustomerUsersMfaBulk = observer(CustomerUsersMfaBulkBase);
